import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const numberPattern = /[+-]?\d(?:[\d.,'’\s]*\d)?/u

const getDecimalSeparator = value => {
  const punctuation = Array.from(value.matchAll(/[.,]/g))
  if (!punctuation.length) return ''

  const types = new Set(punctuation.map(match => match[0]))
  const last = punctuation[punctuation.length - 1]
  const decimalPlaces = value.slice(last.index + 1).replace(/\D/g, '').length

  if (types.size > 1) return last[0]
  if (punctuation.length === 1 && decimalPlaces !== 3) return last[0]
  if (punctuation.length > 1 && decimalPlaces !== 3) return last[0]

  return ''
}

const parseValue = text => {
  const match = text.match(numberPattern)
  if (!match) return null

  const raw = match[0].trimEnd()
  const prefix = text.slice(0, match.index)
  const suffix = text.slice(match.index + raw.length)
  const unsigned = raw.replace(/^[+-]/, '')
  const decimalSeparator = getDecimalSeparator(unsigned)
  const decimalIndex = decimalSeparator ? unsigned.lastIndexOf(decimalSeparator) : -1
  const integerPart = decimalIndex >= 0 ? unsigned.slice(0, decimalIndex) : unsigned
  const decimalPart = decimalIndex >= 0 ? unsigned.slice(decimalIndex + 1).replace(/\D/g, '') : ''
  const groupingMatch = integerPart.match(/[^\d]/)
  const groupingSeparator = groupingMatch?.[0] || ''
  const normalized = `${integerPart.replace(/\D/g, '') || '0'}${decimalPart ? `.${decimalPart}` : ''}`
  const value = Number(normalized) * (raw.startsWith('-') ? -1 : 1)

  if (!Number.isFinite(value)) return null

  return {
    value,
    prefix,
    suffix,
    decimalSeparator,
    decimalPlaces: decimalPart.length,
    groupingSeparator,
    showPositiveSign: raw.startsWith('+')
  }
}

const formatValue = (value, format) => {
  const absolute = Math.abs(value).toFixed(format.decimalPlaces)
  const [integer, decimal] = absolute.split('.')
  const groupedInteger = format.groupingSeparator ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, format.groupingSeparator) : integer
  const sign = value < 0 ? '-' : format.showPositiveSign ? '+' : ''
  const fraction = decimal ? `${format.decimalSeparator}${decimal}` : ''

  return `${format.prefix}${sign}${groupedInteger}${fraction}${format.suffix}`
}

const CountUp = {
  instances: new Map(),

  init() {
    document.querySelectorAll('[data-count-up]').forEach(element => {
      if (this.instances.has(element) || element.closest('.editor-styles-wrapper')) return

      const original = element.textContent
      const format = parseValue(original)
      if (!format) return

      const media = gsap.matchMedia()
      this.instances.set(element, media)
      media.add('(prefers-reduced-motion: no-preference)', () => this.create(element, original, format))
    })
  },

  create(element, original, format) {
    const state = { value: 0 }
    const render = () => {
      element.textContent = formatValue(state.value, format)
    }
    const animation = gsap.to(state, {
      value: format.value,
      duration: 1.8,
      ease: 'power3.out',
      paused: true,
      onUpdate: render,
      onComplete: () => {
        element.textContent = original
      }
    })
    const trigger = ScrollTrigger.create({
      trigger: element,
      start: 'top 90%',
      once: true,
      onEnter: () => animation.play()
    })

    render()

    return () => {
      trigger.kill()
      animation.revert()
      element.textContent = original
    }
  },

  destroy() {
    this.instances.forEach(media => media.revert())
    this.instances.clear()
  }
}

export default CountUp
